import { ICertificationStatusReportRowItem } from '../CertificationStatusReportTable/CertificationStatusReportTable';
import axios from 'axios';
import { t } from 'i18next';
import { isNil } from 'lodash';
import { DateTime } from 'luxon';
import { CertificationRecord } from '../types/types';
import { isStringInUnionList } from 'utils/stringUtils';
import {
  fileNameExtensionToFileExtensionLookupCertificationAttachment,
  fileTypeToFileExtensionLookupCertificationAttachment,
  supportedCertificationAttachmentFileExtensions,
  supportedCertificationAttachmentFileType,
  TSupportedCertificationAttachmentFileExtensions,
  TSupportedCertificationAttachmentFileType,
  unsupportedCertificationAttachmentFileTypeMessage,
} from '../constants/constants';

export const mapCertificationRecordToTableData = (
  records: CertificationRecord[]
): ICertificationStatusReportRowItem[] => {
  return records.map((record) => ({
    id: record.id,
    member: record.member,
    certification: record.certification,
    issueDate: DateTime.fromISO(record.renewedOn).toFormat('LLL dd, yyyy'),
    expirationDate: record.expiresOn ? DateTime.fromISO(record.expiresOn).toFormat('LLL dd, yyyy') : '',
    renewal: DateTime.fromISO(record.updatedOn).toFormat('LLL dd, yyyy'),
    certificationRecord: record,
  }));
};

export const getExpirationDate = (issueDate: Date, months: number): DateTime => {
  const expirationDate = DateTime.fromJSDate(issueDate);
  return expirationDate.plus({ months });
};

export function isSupportedCertificationAttachmentFileType(
  value: string
): value is TSupportedCertificationAttachmentFileType {
  return isStringInUnionList<TSupportedCertificationAttachmentFileType>(
    value,
    supportedCertificationAttachmentFileType
  );
}

export function getCertificationAttachmentErrorLabel(error: unknown): string {
  if (error instanceof Error && error.message === unsupportedCertificationAttachmentFileTypeMessage) {
    return t('That file type is not supported. Supported types are .png, .jpg, .jpeg, .pdf, and .docx.');
  }

  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  if (!isNil(status)) {
    return t('The document could not be uploaded (error {{status}}). Please try again.', { status });
  }

  return t('Unable to save the certification. Please try again.');
}

export function getCertificationAttachmentFileType(
  file: File
): TSupportedCertificationAttachmentFileExtensions | null {
  if (isSupportedCertificationAttachmentFileType(file.type)) {
    return fileTypeToFileExtensionLookupCertificationAttachment[file.type];
  }

  const fileNameExtension = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (
    isStringInUnionList<TSupportedCertificationAttachmentFileExtensions>(
      fileNameExtension,
      supportedCertificationAttachmentFileExtensions
    )
  ) {
    return fileNameExtensionToFileExtensionLookupCertificationAttachment[fileNameExtension];
  }

  return null;
}
